import React, { useState, useEffect } from 'react'
import {
  useDataProvider,
  useNotify,
  Button,
  useRefresh,
  useRecordContext,
} from 'react-admin'
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material'
import OrganisationRiskQuestionnaire from './OrganisationRiskQuestionnaire'
import QuestionnaireFormLayout from '../components/QuestionnaireFormLayout'

// Define the questionnaire types
export enum QuestionnaireType {
  PRELIMINARY = 'preliminary',
  RISK = 'risk',
}

// Define the risk questionnaire type
interface RiskQuestionnaire {
  id: number
  name: string
  description: string
  type: QuestionnaireType
  questionnaire: any[]
  min_risk_score?: number
  max_risk_score?: number
  expiry_minutes?: number
  organisation_id: number
  created_at: string
  updated_at: string
}

// Helper function to format expiry minutes into a human-readable format
const formatExpiryTime = (minutes?: number): string => {
  if (minutes === undefined) return 'Not set'

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60

  if (hours === 0) {
    return `${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`
  }
  if (remainingMinutes === 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''}`
  }
  return `${hours} hour${hours !== 1 ? 's' : ''} ${remainingMinutes} minute${
    remainingMinutes !== 1 ? 's' : ''
  }`
}

const OrganisationRiskQuestionnaireDirectory = () => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const record = useRecordContext()

  // State to store the questionnaires
  const [riskQuestionnaires, setRiskQuestionnaires] = useState<
    RiskQuestionnaire[]
  >([])
  const [loading, setLoading] = useState(true)
  const [selectedForm, setSelectedForm] = useState<RiskQuestionnaire | null>(
    null
  )
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [newFormName, setNewFormName] = useState('')
  const [newFormDescription, setNewFormDescription] = useState('')
  const [newFormType, setNewFormType] = useState<QuestionnaireType>(
    QuestionnaireType.RISK
  )
  const [isCreating, setIsCreating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDuplicating, setIsDuplicating] = useState(false)
  const [questionnaireToDelete, setQuestionnaireToDelete] = useState<
    number | null
  >(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [questionnaireToView, setQuestionnaireToView] =
    useState<RiskQuestionnaire | null>(null)

  // Load the organisation's questionnaires
  useEffect(() => {
    const loadRiskQuestionnaires = async () => {
      try {
        setLoading(true)
        const organisationId = record?.id
        const endpoint = organisationId
          ? `questionnaires/organisations/${organisationId}`
          : 'questionnaires/organisations/current'
        const response = await dataProvider.custom(endpoint, {
          method: 'GET',
        })

        // Convert string type values to enum values
        const questionnaires = (response.data || []).map((questionnaire) => {
          if (questionnaire.type) {
            return {
              ...questionnaire,
              type:
                questionnaire.type === 'preliminary'
                  ? QuestionnaireType.PRELIMINARY
                  : QuestionnaireType.RISK,
            }
          }
          return questionnaire
        })

        setRiskQuestionnaires(questionnaires)
      } catch (error) {
        console.error('Error loading questionnaires:', error)
        notify('Error loading questionnaires', { type: 'error' })
        setRiskQuestionnaires([])
      } finally {
        setLoading(false)
      }
    }

    loadRiskQuestionnaires()
  }, [dataProvider, notify, record])

  // Reset the create form
  const resetCreateForm = () => {
    setNewFormName('')
    setNewFormDescription('')
    setNewFormType(QuestionnaireType.RISK)
  }

  // Handle creating a new risk questionnaire
  const handleCreateForm = async () => {
    if (!newFormName.trim()) {
      notify('Form name is required', { type: 'warning' })
      return
    }

    try {
      setIsCreating(true)
      const organisationId = record?.id
      const endpoint = organisationId
        ? `questionnaires/organisations/${organisationId}`
        : 'questionnaires/organisations/current'

      const payload = {
        name: newFormName,
        description: newFormDescription,
        type: newFormType,
        questionnaire: [], // Initialize with an empty questionnaire
        expiry_minutes: 60, // Default to 60 minutes (1 hour)
      }

      const createResponse = await dataProvider.custom(endpoint, {
        method: 'POST',
        body: JSON.stringify(payload),
      })

      notify('Questionnaire created successfully', { type: 'success' })
      setIsCreateDialogOpen(false)
      resetCreateForm()
      refresh()

      // Reload the questionnaires
      const response = await dataProvider.custom(endpoint, {
        method: 'GET',
      })

      // Convert string type values to enum values
      const questionnaires = (response.data || []).map((questionnaire) => {
        if (questionnaire.type) {
          return {
            ...questionnaire,
            type:
              questionnaire.type === 'preliminary'
                ? QuestionnaireType.PRELIMINARY
                : QuestionnaireType.RISK,
          }
        }
        return questionnaire
      })

      setRiskQuestionnaires(questionnaires)

      // Find the newly created questionnaire
      // If the API returns the created questionnaire directly, use that
      if (createResponse.data && createResponse.data.id) {
        const newQuestionnaire = createResponse.data
        // Convert type if needed
        if (newQuestionnaire.type) {
          newQuestionnaire.type =
            newQuestionnaire.type === 'preliminary'
              ? QuestionnaireType.PRELIMINARY
              : QuestionnaireType.RISK
        }
        // Open the edit form for the newly created questionnaire
        setSelectedForm(newQuestionnaire)
        setIsModalOpen(true)
      } else {
        // Otherwise, try to find the newly created questionnaire in the reloaded list
        // This is a fallback in case the API doesn't return the created questionnaire
        const newQuestionnaire = questionnaires.find(
          (q) =>
            q.name === newFormName &&
            q.description === newFormDescription &&
            q.type === newFormType
        )

        if (newQuestionnaire) {
          // Open the edit form for the newly created questionnaire
          setSelectedForm(newQuestionnaire)
          setIsModalOpen(true)
        }
      }
    } catch (error) {
      console.error('Error creating questionnaire:', error)
      // Try to extract the specific error message from the backend response
      let errorMessage = 'Error creating questionnaire'
      if (error && typeof error === 'object') {
        if (
          'body' in error &&
          error.body &&
          typeof error.body === 'object' &&
          'detail' in error.body
        ) {
          errorMessage = error.body.detail as string
        } else if ('message' in error && typeof error.message === 'string') {
          errorMessage = error.message
        }
      }
      notify(errorMessage, { type: 'error' })
    } finally {
      setIsCreating(false)
    }
  }

  // Show delete confirmation dialog
  const showDeleteConfirmation = (formId: number) => {
    setQuestionnaireToDelete(formId)
    setIsDeleteDialogOpen(true)
  }

  // Handle deleting a risk questionnaire
  const handleDeleteForm = async () => {
    if (!questionnaireToDelete) return

    try {
      setIsDeleting(true)
      const organisationId = record?.id
      const endpoint = organisationId
        ? `questionnaires/${questionnaireToDelete}/organisations/${organisationId}`
        : `questionnaires/${questionnaireToDelete}/organisations/current`

      await dataProvider.custom(endpoint, {
        method: 'DELETE',
      })

      notify('Questionnaire deleted successfully', { type: 'success' })

      // Update the local state to remove the deleted form
      setRiskQuestionnaires(
        riskQuestionnaires.filter((form) => form.id !== questionnaireToDelete)
      )

      // Close the dialog
      setIsDeleteDialogOpen(false)
      setQuestionnaireToDelete(null)
    } catch (error) {
      console.error('Error deleting questionnaire:', error)
      notify('Error deleting questionnaire', { type: 'error' })
    } finally {
      setIsDeleting(false)
    }
  }

  // Handle opening the edit modal
  const handleEditForm = (form: RiskQuestionnaire) => {
    setSelectedForm(form)
    setIsModalOpen(true)
  }

  // Handle opening the view modal
  const handleViewQuestionnaire = (questionnaire: RiskQuestionnaire) => {
    setQuestionnaireToView(questionnaire)
    setIsViewModalOpen(true)
  }

  // Handle duplicating a questionnaire
  const handleDuplicateQuestionnaire = async (questionnaireId: number) => {
    try {
      setIsDuplicating(true)
      const organisationId = record?.id
      const endpoint = organisationId
        ? `questionnaires/${questionnaireId}/organisations/${organisationId}/duplicate`
        : `questionnaires/${questionnaireId}/organisations/current/duplicate`

      const response = await dataProvider.custom(endpoint, {
        method: 'POST',
      })

      notify('Questionnaire duplicated successfully', { type: 'success' })

      // Get the duplicated questionnaire from the response
      const duplicatedQuestionnaire = response.data

      // Convert type if needed
      if (duplicatedQuestionnaire && duplicatedQuestionnaire.type) {
        duplicatedQuestionnaire.type =
          duplicatedQuestionnaire.type === 'preliminary'
            ? QuestionnaireType.PRELIMINARY
            : QuestionnaireType.RISK
      }

      // Reload the questionnaires to include the new duplicate
      const reloadEndpoint = organisationId
        ? `questionnaires/organisations/${organisationId}`
        : 'questionnaires/organisations/current'

      const reloadResponse = await dataProvider.custom(reloadEndpoint, {
        method: 'GET',
      })

      // Convert string type values to enum values
      const questionnaires = (reloadResponse.data || []).map(
        (questionnaire) => {
          if (questionnaire.type) {
            return {
              ...questionnaire,
              type:
                questionnaire.type === 'preliminary'
                  ? QuestionnaireType.PRELIMINARY
                  : QuestionnaireType.RISK,
            }
          }
          return questionnaire
        }
      )

      setRiskQuestionnaires(questionnaires)

      // Open the duplicated questionnaire for editing
      if (duplicatedQuestionnaire) {
        setSelectedForm(duplicatedQuestionnaire)
        setIsModalOpen(true)
      }
    } catch (error) {
      console.error('Error duplicating questionnaire:', error)
      notify('Error duplicating questionnaire', { type: 'error' })
    } finally {
      setIsDuplicating(false)
    }
  }

  // Handle closing the edit modal
  const handleCloseModal = (updatedQuestionnaire?: RiskQuestionnaire) => {
    setIsModalOpen(false)

    if (updatedQuestionnaire && selectedForm) {
      // Update the questionnaire in the local state
      setRiskQuestionnaires((prevQuestionnaires) =>
        prevQuestionnaires.map((q) =>
          q.id === updatedQuestionnaire.id ? updatedQuestionnaire : q
        )
      )
    }

    setSelectedForm(null)
    // Refresh the list after editing
    refresh()
  }

  // Wrapper for Dialog onClose that adapts to expected signature
  const handleDialogClose = (
    _: {},
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    handleCloseModal()
  }

  // Handle closing the view modal
  const handleCloseViewModal = () => {
    setIsViewModalOpen(false)
    setQuestionnaireToView(null)
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'visible' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Questionnaires</Typography>
        <Button
          label="Create New Questionnaire"
          onClick={() => setIsCreateDialogOpen(true)}
          startIcon={<AddIcon />}
        />
      </Box>

      {riskQuestionnaires.length === 0 ? (
        <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No questionnaires found. Create your first questionnaire to get
            started.
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={1}>
          <List>
            {riskQuestionnaires.map((questionnaire, index) => (
              <React.Fragment key={questionnaire.id}>
                {index > 0 && <Divider />}
                <ListItem>
                  <ListItemText
                    primary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          fontWeight="bold"
                          sx={{ mr: 1 }}
                        >
                          Name:
                        </Typography>
                        {questionnaire.name}
                      </>
                    }
                    secondary={
                      <>
                        {questionnaire.description && (
                          <Typography
                            component="span"
                            variant="body2"
                            color="textSecondary"
                            sx={{ display: 'block' }}
                          >
                            <Typography
                              component="span"
                              variant="body2"
                              fontWeight="bold"
                              sx={{ mr: 1 }}
                            >
                              Description:
                            </Typography>
                            {questionnaire.description}
                          </Typography>
                        )}
                        <Typography
                          component="span"
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            display: 'block',
                            mt: questionnaire.description ? 1 : 0,
                          }}
                        >
                          Type:{' '}
                          {questionnaire.type === QuestionnaireType.PRELIMINARY
                            ? 'Prequalification Questionnaire'
                            : 'Risk Assessment'}
                        </Typography>
                        <Typography
                          component="span"
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            display: 'block',
                          }}
                        >
                          Questionnaire ID: {questionnaire.id}
                        </Typography>
                        {questionnaire.type === QuestionnaireType.RISK && (
                          <Typography
                            component="span"
                            variant="body2"
                            color="textSecondary"
                            sx={{
                              display: 'block',
                            }}
                          >
                            {questionnaire.min_risk_score !== undefined &&
                            questionnaire.max_risk_score !== undefined ? (
                              <>
                                Risk Score Range: {questionnaire.min_risk_score}{' '}
                                - {questionnaire.max_risk_score}
                              </>
                            ) : (
                              <>Risk Score Range: Not calculated</>
                            )}
                          </Typography>
                        )}
                        <Typography
                          component="span"
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            display: 'block',
                          }}
                        >
                          Expiry Time:{' '}
                          {formatExpiryTime(questionnaire.expiry_minutes)}
                        </Typography>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="view"
                      onClick={() => handleViewQuestionnaire(questionnaire)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="duplicate"
                      onClick={() =>
                        handleDuplicateQuestionnaire(questionnaire.id)
                      }
                      disabled={isDuplicating}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      onClick={() => handleEditForm(questionnaire)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => showDeleteConfirmation(questionnaire.id)}
                      disabled={isDeleting}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* Create Form Dialog */}
      <Dialog
        open={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(false)
          resetCreateForm()
        }}
        aria-labelledby="create-form-dialog-title"
      >
        <DialogTitle id="create-form-dialog-title">
          Create New Questionnaire
          <IconButton
            aria-label="close"
            onClick={() => {
              setIsCreateDialogOpen(false)
              resetCreateForm()
            }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel id="questionnaire-type-label">
              Questionnaire Type
            </InputLabel>
            <Select
              labelId="questionnaire-type-label"
              id="questionnaire-type"
              value={newFormType}
              label="Questionnaire Type"
              onChange={(e) =>
                setNewFormType(e.target.value as QuestionnaireType)
              }
            >
              <MenuItem value={QuestionnaireType.PRELIMINARY}>
                Prequalification Questionnaire
              </MenuItem>
              <MenuItem value={QuestionnaireType.RISK}>
                Risk Assessment
              </MenuItem>
            </Select>
            <FormHelperText>
              {newFormType === QuestionnaireType.PRELIMINARY
                ? 'Prequalification Questionnaires have a maximum of 10 questions with Yes/No answers.'
                : 'Risk Assessments have a maximum of 100 questions with dropdown answers.'}
            </FormHelperText>
          </FormControl>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Questionnaire Name"
            type="text"
            fullWidth
            value={newFormName}
            onChange={(e) => setNewFormName(e.target.value)}
          />
          <TextField
            margin="dense"
            id="description"
            label="Questionnaire Description"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={newFormDescription}
            onChange={(e) => setNewFormDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            label="Cancel"
            onClick={() => {
              setIsCreateDialogOpen(false)
              resetCreateForm()
            }}
            disabled={isCreating}
          />
          <Button
            label={isCreating ? 'Creating...' : 'Create'}
            onClick={handleCreateForm}
            disabled={isCreating || !newFormName.trim()}
          />
        </DialogActions>
      </Dialog>

      {/* Edit Form Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="md"
        aria-labelledby="edit-form-dialog-title"
      >
        <DialogTitle id="edit-form-dialog-title">
          Edit Questionnaire: {selectedForm?.name}
          <IconButton
            aria-label="close"
            onClick={() => handleCloseModal()}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedForm && (
            <OrganisationRiskQuestionnaire
              questionnaireId={selectedForm.id}
              isModal
              onClose={handleCloseModal}
              initialQuestionnaireType={selectedForm.type}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* View Questionnaire Modal */}
      <Dialog
        open={isViewModalOpen}
        onClose={handleCloseViewModal}
        fullWidth
        maxWidth="md"
        aria-labelledby="view-questionnaire-dialog-title"
      >
        <DialogTitle id="view-questionnaire-dialog-title">
          View Questionnaire: {questionnaireToView?.name}
          <IconButton
            aria-label="close"
            onClick={handleCloseViewModal}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {questionnaireToView && (
            <QuestionnaireFormLayout
              questionnaireSections={questionnaireToView.questionnaire || []}
              questionnaireName={questionnaireToView.name}
              questionnaireDescription={questionnaireToView.description}
              readOnly
              renderField={(field) => {
                // Custom renderer that shows the field type (yes/no or dropdown) but disables interaction
                // For yes/no fields (used in prequalification questionnaires)
                if (field.fieldType === 'yesno') {
                  return (
                    <TextField
                      select
                      label={field.label}
                      name={field.source}
                      fullWidth
                      variant="outlined"
                      required={field.required}
                      disabled={false}
                      helperText={field.required ? 'Required' : 'Optional'}
                      InputProps={{
                        readOnly: false,
                      }}
                      SelectProps={{
                        native: true,
                      }}
                    >
                      <option value="">Select an option</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </TextField>
                  )
                }

                // For attachment fields
                if (field.fieldType === 'attachment') {
                  return (
                    <Box>
                      <Typography variant="body1" gutterBottom>
                        {field.label}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Attachment question - Allows vendors to upload PDF or
                        Word documents
                      </Typography>
                    </Box>
                  )
                }

                // For text box fields
                if (field.fieldType === 'text') {
                  return (
                    <Box>
                      <Typography variant="body1" gutterBottom>
                        {field.label}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Text Box question - Allows vendors to enter free-form
                        text responses
                      </Typography>
                    </Box>
                  )
                }

                // For dropdown fields (used in risk assessments)
                if (field.fieldType === 'dropdown') {
                  return (
                    <TextField
                      select
                      label={field.label}
                      name={field.source}
                      fullWidth
                      variant="outlined"
                      required={field.required}
                      disabled={false}
                      helperText={field.required ? 'Required' : 'Optional'}
                      InputProps={{
                        readOnly: false,
                      }}
                      SelectProps={{
                        native: true,
                      }}
                    >
                      <option value="">Select an option</option>
                      {field.options && field.options.length > 0 ? (
                        field.options.map((option, index) => (
                          <option key={index} value={option.text}>
                            {option.text}
                            {option.score !== undefined
                              ? ` (Score: ${
                                  typeof option.score === 'number'
                                    ? option.score.toFixed(1)
                                    : option.score
                                })`
                              : ''}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>
                          No options available
                        </option>
                      )}
                    </TextField>
                  )
                }

                // Default text field for other fields
                return (
                  <TextField
                    label={field.label}
                    name={field.source}
                    fullWidth
                    variant="outlined"
                    required={field.required}
                    disabled
                    helperText={field.required ? 'Required' : 'Optional'}
                  />
                )
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            label="Close"
            onClick={handleCloseViewModal}
            variant="outlined"
          />
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false)
          setQuestionnaireToDelete(null)
        }}
        aria-labelledby="delete-questionnaire-dialog-title"
      >
        <DialogTitle id="delete-questionnaire-dialog-title">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this questionnaire? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            label="Cancel"
            onClick={() => {
              setIsDeleteDialogOpen(false)
              setQuestionnaireToDelete(null)
            }}
            disabled={isDeleting}
          />
          <Button
            label={isDeleting ? 'Deleting...' : 'Delete'}
            onClick={handleDeleteForm}
            disabled={isDeleting}
            color="primary"
          />
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default OrganisationRiskQuestionnaireDirectory
